import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';

import { SiteState } from 'src/app/shared/models';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { selectCropYear } from 'src/app/store/selectors';

import {
  PlantingQuestions,
  PlantingQuestionsRequest,
  ProcessedAnswersWithForm,
} from 'src/app/tga/shared/models/models';
import { fetchPlantingQuestions, loadCropFields, postPlantingQuestions } from 'src/app/tga/store/tga.actions';
import { selectCropFieldById, selectPlantingQuestions } from 'src/app/tga/store/tga.selectors';

import { SpecificPlantingQuestionsService } from './specific-planting-questions.service';
import { TgaInputType, TGAQuestion } from '../../../models/question-models';
import { SpecificQuestionnaireComponent } from '../shared/specific-questionnaire/specific-questionnaire.component';
import { TranslatePipe } from '@next/core-lib/i18n';

@Component({
  selector: 'app-specific-planting-questionnaire',
  templateUrl: './specific-planting-questionnaire.component.html',
  imports: [SpecificQuestionnaireComponent, TranslatePipe],
})
export class SpecificPlantingQuestionnaireComponent implements OnInit {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<SiteState>);
  private readonly route = inject(ActivatedRoute);
  private readonly questionService = inject(SpecificPlantingQuestionsService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  readonly cropFieldId = signal<number | null>(null);
  readonly cropYear = signal<number | null>(null);
  readonly cropField = signal<CropField | undefined>(undefined);

  readonly plantingQuestions = signal<TGAQuestion<TgaInputType>[]>([]);
  readonly plantingQuestions$ = this.store.select(selectPlantingQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);

  private readonly params$ = this.route.params.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(this.questionService.extractRouteParams.bind(this.questionService)),
    filter(this.questionService.validateRouteParams.bind(this.questionService)),
    tap(({ cropFieldId, cropYear }) => {
      this.cropFieldId.set(cropFieldId);
      this.cropYear.set(cropYear);
    }),
  ) as ReturnType<typeof this.route.params.pipe<{ cropFieldId: number; cropYear: number }>>;

  readonly cropField$ = this.params$.pipe(
    switchMap((params) => this.store.select(selectCropFieldById(params.cropFieldId))),
    tap((cropfieldWithStatus) => {
      const cropYear = this.cropYear();
      if (!cropfieldWithStatus && cropYear !== null) {
        this.selectedCropYear$.pipe(take(1)).subscribe((selectedCropYear) => {
          this.store.dispatch(loadCropFields({ cropYear: selectedCropYear }));
        });
      }
      this.cropField.set(cropfieldWithStatus?.cropField ?? undefined);
    }),
  );

  readonly questions$ = combineLatest([
    this.cropField$.pipe(filter((cropField) => cropField !== null)),
    this.plantingQuestions$,
  ]).pipe(
    tap(() => {
      this.questionService.initializePlantingQuestions();
    }),
    map(() => {
      return this.questionService.initValues();
    }),
  );

  ngOnInit(): void {
    this.initializeParamsAndQuestions();
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;

    if (formGroup) {
      this.unsavedChangesService.registerForm(formGroup);
    }

    const cropFieldId = this.cropFieldId();
    const cropYear = this.cropYear();

    if (cropFieldId && cropYear) {
      const longStorageValue = processedAnswers['longStorageExpected'] as string | null;
      let longStorageExpected: boolean | null = null;

      if (longStorageValue === 'true') {
        longStorageExpected = true;
      } else if (longStorageValue === 'false') {
        longStorageExpected = false;
      }

      const improvementTopics = this.questionService.processImprovementTopics(processedAnswers);

      const plantingQuestions: PlantingQuestions = {
        ...improvementTopics,
        longStorageExpected,
      };

      const answersPayload: PlantingQuestionsRequest = {
        ...plantingQuestions,
        cropFieldId: cropFieldId,
        cropYear: new Date().getFullYear(),
      };

      this.store.dispatch(
        postPlantingQuestions({
          answers: answersPayload,
        }),
      );

      this.unsavedChangesService.markFormCompleted();
    }
  }

  private initializeParamsAndQuestions() {
    this.params$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) =>
          this.questions$.pipe(
            take(1),
            tap(() => this.checkAndFetchMissingQuestions(params)),
          ),
        ),
      )
      .subscribe();

    this.questions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.plantingQuestions.set(questions);
      });
  }

  private checkAndFetchMissingQuestions(params: { cropFieldId: number; cropYear: number }) {
    this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(
        fetchPlantingQuestions({
          cropFieldId: params.cropFieldId,
          cropYear,
        }),
      );
    });
  }
}
